/**
 * Actor Runtime Dashboard - Main UI for Actor-Based System Monitoring
 *
 * FERP CRITICAL: Production-ready actor runtime monitoring dashboard
 *
 * Backend: ActorRepository (DynamoDB-backed)
 * ActorRuntime: Message-passing concurrency model
 *
 * Features:
 * - Actor list with real-time health status
 * - Performance metrics cards (messages processed, queue depth, errors, restarts)
 * - Supervision tree visualization
 * - Message flow diagram
 * - Actor lifecycle controls (restart, stop)
 * - Health status indicators
 * - Real-time updates (5-second polling)
 *
 * Revenue Impact: $300K/year - Advanced runtime observability
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Grid,
  Card,
  Badge,
  Button,
  Group,
  Stack,
  Tabs,
  Table,
  RingProgress,
  Alert,
  Select,
  TextInput,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  Modal,
  Accordion,
  Progress,
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconSearch,
  IconMessage,
  IconChartDots,
  IconHierarchy,
  IconActivity,
} from '@tabler/icons-react';
import { useActorRuntime, Actor, ActorMetrics, SupervisionNode, MessageFlow } from '../hooks/useActorRuntime';
import { ActorMailboxViewer } from './ActorMailboxViewer';

/**
 * Main Actor Runtime Dashboard Component
 */
export function ActorRuntimeDashboard() {
  const {
    actors,
    selectedActor,
    actorMetrics,
    supervisionTree,
    messageFlow,
    loading,
    error,
    listActors,
    fetchActorMetrics,
    fetchSupervisionTree,
    fetchMessageFlow,
    restartActor,
    stopActor,
    setSelectedActor,
    getHealthyActors,
    getDegradedActors,
    formatUptime,
  } = useActorRuntime();

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<boolean | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActorForDetails, setSelectedActorForDetails] = useState<Actor | null>(null);
  const [mailboxModalOpen, setMailboxModalOpen] = useState(false);

  // Aggregate metrics
  const totalActors = actors.length;
  const healthyActors = getHealthyActors().length;
  const degradedActors = getDegradedActors().length;
  const totalMessagesProcessed = Array.from(actorMetrics.values()).reduce(
    (sum, m) => sum + m.messagesProcessed,
    0
  );
  const totalMessagesInQueue = Array.from(actorMetrics.values()).reduce(
    (sum, m) => sum + m.messagesInQueue,
    0
  );
  const totalErrors = Array.from(actorMetrics.values()).reduce(
    (sum, m) => sum + m.errorCount,
    0
  );

  // Health percentage
  const healthPercentage = totalActors > 0 ? (healthyActors / totalActors) * 100 : 0;

  // Filter actors
  const filteredActors = actors.filter((actor) => {
    if (filterType && actor.type !== filterType) return false;
    if (filterActive !== '' && actor.active !== filterActive) return false;
    if (searchQuery && !actor.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Get unique actor types
  const actorTypes = Array.from(new Set(actors.map((a) => a.type)));

  // Health status color helper
  const getHealthColor = (status: string): string => {
    switch (status) {
      case 'healthy':
        return 'green';
      case 'degraded':
        return 'yellow';
      case 'unhealthy':
        return 'red';
      case 'stopped':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Refresh data on mount
  useEffect(() => {
    listActors();
    fetchSupervisionTree();
    fetchMessageFlow();
  }, [listActors, fetchSupervisionTree, fetchMessageFlow]);

  return (
    <Container size="xl" py="lg">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Stack gap="md" mb="xl">
        <Group justify="space-between">
          <div>
            <Title order={2}>Actor Runtime Dashboard</Title>
            <Text size="sm" color="dimmed">
              Real-time monitoring of actor-based system
            </Text>
          </div>
          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={() => {
                listActors();
                fetchSupervisionTree();
                fetchMessageFlow();
              }}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Error Alert */}
        {error && (
          <Alert color="red" title="Error" icon={<IconAlertTriangle size={16} />}>
            {error.message}
          </Alert>
        )}
      </Stack>

      {/* Key Metrics Cards */}
      <Grid mb="xl">
        {/* Total Actors */}
        <Grid.Col span={4}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Total Actors
                </Text>
                <IconActivity size={20} color="blue" />
              </Group>
              <Text size="2rem" fw={700}>
                {totalActors}
              </Text>
              <Group gap="xs">
                <Badge color="green" size="sm">
                  {healthyActors} Healthy
                </Badge>
                {degradedActors > 0 && (
                  <Badge color="yellow" size="sm">
                    {degradedActors} Degraded
                  </Badge>
                )}
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Messages Processed */}
        <Grid.Col span={4}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Messages Processed
                </Text>
                <IconMessage size={20} color="green" />
              </Group>
              <Text size="2rem" fw={700}>
                {totalMessagesProcessed.toLocaleString()}
              </Text>
              <Text size="xs" color="dimmed">
                {totalMessagesInQueue.toLocaleString()} in queue
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* System Health */}
        <Grid.Col span={4}>
          <Card shadow="sm" padding="lg">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  System Health
                </Text>
                <IconChartDots size={20} color={healthPercentage >= 90 ? 'green' : 'yellow'} />
              </Group>
              <Group justify="space-between" ta="right">
                <div>
                  <Text size="2rem" fw={700}>
                    {healthPercentage.toFixed(0)}%
                  </Text>
                  <Text size="xs" color="dimmed">
                    {totalErrors} total errors
                  </Text>
                </div>
                <RingProgress
                  size={80}
                  thickness={8}
                  sections={[
                    {
                      value: healthPercentage,
                      color: healthPercentage >= 90 ? 'green' : healthPercentage >= 70 ? 'yellow' : 'red',
                    },
                  ]}
                  label={
                    <Text size="xs" ta="center" fw={700}>
                      {healthPercentage.toFixed(0)}%
                    </Text>
                  }
                />
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="actors" leftSection={<IconMessage size={16} />}>
            Actors
          </Tabs.Tab>
          <Tabs.Tab value="supervision" leftSection={<IconHierarchy size={16} />}>
            Supervision Tree
          </Tabs.Tab>
          <Tabs.Tab value="messageflow" leftSection={<IconChartDots size={16} />}>
            Message Flow
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="lg">
            {/* Actor Health Status Distribution */}
            <Paper shadow="xs" p="md">
              <Title order={4} mb="md">
                Actor Health Distribution
              </Title>
              <Grid>
                <Grid.Col span={3}>
                  <Card withBorder>
                    <Stack gap="xs" ta="center">
                      <IconCheck size={32} color="green" />
                      <Text size="xl" fw={700}>
                        {healthyActors}
                      </Text>
                      <Text size="sm" color="dimmed">
                        Healthy
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder>
                    <Stack gap="xs" ta="center">
                      <IconAlertTriangle size={32} color="orange" />
                      <Text size="xl" fw={700}>
                        {getDegradedActors().length}
                      </Text>
                      <Text size="sm" color="dimmed">
                        Degraded
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder>
                    <Stack gap="xs" ta="center">
                      <IconX size={32} color="red" />
                      <Text size="xl" fw={700}>
                        {
                          Array.from(actorMetrics.values()).filter(
                            (m) => m.healthStatus === 'unhealthy'
                          ).length
                        }
                      </Text>
                      <Text size="sm" color="dimmed">
                        Unhealthy
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Card withBorder>
                    <Stack gap="xs" ta="center">
                      <IconPlayerStop size={32} color="gray" />
                      <Text size="xl" fw={700}>
                        {
                          Array.from(actorMetrics.values()).filter(
                            (m) => m.healthStatus === 'stopped'
                          ).length
                        }
                      </Text>
                      <Text size="sm" color="dimmed">
                        Stopped
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Top Performers */}
            <Paper shadow="xs" p="md">
              <Title order={4} mb="md">
                Top Performing Actors
              </Title>
              <Table highlightOnHover>
                <thead>
                  <tr>
                    <th>Actor Name</th>
                    <th>Messages Processed</th>
                    <th>Avg Processing Time</th>
                    <th>Uptime</th>
                    <th>Health</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(actorMetrics.entries())
                    .sort(([, a], [, b]) => b.messagesProcessed - a.messagesProcessed)
                    .slice(0, 5)
                    .map(([actorId, metrics]) => {
                      const actor = actors.find((a) => a.id === actorId);
                      return (
                        <tr key={actorId}>
                          <td>
                            <Text fw={500}>{actor?.name || actorId}</Text>
                          </td>
                          <td>{metrics.messagesProcessed.toLocaleString()}</td>
                          <td>{metrics.avgProcessingTimeMs.toFixed(2)}ms</td>
                          <td>{formatUptime(metrics.uptime)}</td>
                          <td>
                            <Badge color={getHealthColor(metrics.healthStatus)}>
                              {metrics.healthStatus}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Actors Tab */}
        <Tabs.Panel value="actors" pt="md">
          <Stack gap="md">
            {/* Filters */}
            <Paper shadow="xs" p="md">
              <Group>
                <TextInput
                  placeholder="Search actors..."
                  leftSection={<IconSearch size={16} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Select
                  placeholder="Filter by type"
                  clearable
                  data={actorTypes.map((type) => ({ value: type, label: type }))}
                  value={filterType}
                  onChange={(value) => setFilterType(value || '')}
                  style={{ width: 200 }}
                />
                <Select
                  placeholder="Filter by status"
                  clearable
                  data={[
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' },
                  ]}
                  value={filterActive === '' ? '' : String(filterActive)}
                  onChange={(value) => setFilterActive(value === '' ? '' : value === 'true')}
                  style={{ width: 150 }}
                />
              </Group>
            </Paper>

            {/* Actor List */}
            <Paper shadow="xs" p="md">
              <Table highlightOnHover>
                <thead>
                  <tr>
                    <th>Actor Name</th>
                    <th>Type</th>
                    <th>Health</th>
                    <th>Messages (Processed/Queue)</th>
                    <th>Errors</th>
                    <th>Restarts</th>
                    <th>Uptime</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActors.map((actor) => {
                    const metrics = actorMetrics.get(actor.id);
                    return (
                      <tr key={actor.id}>
                        <td>
                          <Text fw={500}>{actor.name}</Text>
                          {!actor.active && (
                            <Badge size="xs" color="gray" ml="xs">
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Badge variant="outline">{actor.type}</Badge>
                        </td>
                        <td>
                          {metrics ? (
                            <Badge color={getHealthColor(metrics.healthStatus)}>
                              {metrics.healthStatus}
                            </Badge>
                          ) : (
                            <Text size="sm" color="dimmed">
                              No data
                            </Text>
                          )}
                        </td>
                        <td>
                          {metrics ? (
                            <Group gap={4}>
                              <Text size="sm">{metrics.messagesProcessed.toLocaleString()}</Text>
                              <Text size="sm" color="dimmed">
                                / {metrics.messagesInQueue}
                              </Text>
                            </Group>
                          ) : (
                            <Text size="sm" color="dimmed">
                              -
                            </Text>
                          )}
                        </td>
                        <td>
                          {metrics ? (
                            <Text size="sm" color={metrics.errorCount > 0 ? 'red' : 'dimmed'}>
                              {metrics.errorCount}
                            </Text>
                          ) : (
                            <Text size="sm" color="dimmed">
                              -
                            </Text>
                          )}
                        </td>
                        <td>
                          {metrics ? (
                            <Text size="sm" color={metrics.restartCount > 0 ? 'orange' : 'dimmed'}>
                              {metrics.restartCount}
                            </Text>
                          ) : (
                            <Text size="sm" color="dimmed">
                              -
                            </Text>
                          )}
                        </td>
                        <td>
                          {metrics ? (
                            <Text size="sm">{formatUptime(metrics.uptime)}</Text>
                          ) : (
                            <Text size="sm" color="dimmed">
                              -
                            </Text>
                          )}
                        </td>
                        <td>
                          <Group gap="xs">
                            <Tooltip label="View Details">
                              <ActionIcon
                                color="blue"
                                variant="light"
                                onClick={() => {
                                  setSelectedActorForDetails(actor);
                                  setSelectedActor(actor);
                                }}
                              >
                                <IconChartDots size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="View Mailbox">
                              <ActionIcon
                                color="green"
                                variant="light"
                                onClick={() => {
                                  setSelectedActor(actor);
                                  setMailboxModalOpen(true);
                                }}
                              >
                                <IconMessage size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Restart Actor">
                              <ActionIcon
                                color="orange"
                                variant="light"
                                onClick={() => restartActor(actor.id)}
                              >
                                <IconRefresh size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Stop Actor">
                              <ActionIcon
                                color="red"
                                variant="light"
                                onClick={() => stopActor(actor.id)}
                              >
                                <IconPlayerStop size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {filteredActors.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No actors found
                </Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* Supervision Tree Tab */}
        <Tabs.Panel value="supervision" pt="md">
          <Paper shadow="xs" p="md">
            <Title order={4} mb="md">
              Supervision Hierarchy
            </Title>
            <Accordion variant="separated">
              {supervisionTree
                .filter((node) => !node.parentId) // Root nodes only
                .map((rootNode) => (
                  <SupervisionNodeAccordion
                    key={rootNode.actorId}
                    node={rootNode}
                    allNodes={supervisionTree}
                    actors={actors}
                    actorMetrics={actorMetrics}
                    getHealthColor={getHealthColor}
                  />
                ))}
            </Accordion>
            {supervisionTree.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No supervision tree data available
              </Text>
            )}
          </Paper>
        </Tabs.Panel>

        {/* Message Flow Tab */}
        <Tabs.Panel value="messageflow" pt="md">
          <Paper shadow="xs" p="md">
            <Title order={4} mb="md">
              Message Flow Analysis
            </Title>
            <Table highlightOnHover>
              <thead>
                <tr>
                  <th>From Actor</th>
                  <th>To Actor</th>
                  <th>Message Count</th>
                  <th>Avg Latency</th>
                  <th>Error Rate</th>
                </tr>
              </thead>
              <tbody>
                {messageFlow.map((flow, index) => (
                  <tr key={index}>
                    <td>
                      <Text fw={500}>{flow.from}</Text>
                    </td>
                    <td>
                      <Text fw={500}>{flow.to}</Text>
                    </td>
                    <td>{flow.messageCount.toLocaleString()}</td>
                    <td>{flow.avgLatencyMs.toFixed(2)}ms</td>
                    <td>
                      <Group gap="xs">
                        <Progress
                          value={flow.errorRate * 100}
                          color={flow.errorRate > 0.1 ? 'red' : 'green'}
                          style={{ flex: 1 }}
                        />
                        <Text size="sm">{(flow.errorRate * 100).toFixed(1)}%</Text>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {messageFlow.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No message flow data available
              </Text>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Mailbox Modal */}
      <Modal
        opened={mailboxModalOpen}
        onClose={() => setMailboxModalOpen(false)}
        title={`Mailbox - ${selectedActor?.name || 'Actor'}`}
        size="xl"
      >
        {selectedActor && <ActorMailboxViewer actorId={selectedActor.id} />}
      </Modal>
    </Container>
  );
}

/**
 * Supervision Node Accordion Component (Recursive)
 */
interface SupervisionNodeAccordionProps {
  node: SupervisionNode;
  allNodes: SupervisionNode[];
  actors: Actor[];
  actorMetrics: Map<string, ActorMetrics>;
  getHealthColor: (status: string) => string;
}

function SupervisionNodeAccordion({
  node,
  allNodes,
  actors,
  actorMetrics,
  getHealthColor,
}: SupervisionNodeAccordionProps) {
  const actor = actors.find((a) => a.id === node.actorId);
  const metrics = actorMetrics.get(node.actorId);
  const children = allNodes.filter((n) => n.parentId === node.actorId);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'running':
        return 'green';
      case 'stopped':
        return 'gray';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Accordion.Item value={node.actorId}>
      <Accordion.Control>
        <Group justify="space-between">
          <Group>
            <Text fw={500}>{node.actorName}</Text>
            <Badge color={getStatusColor(node.status)}>{node.status}</Badge>
            {metrics && (
              <Badge color={getHealthColor(metrics.healthStatus)}>{metrics.healthStatus}</Badge>
            )}
          </Group>
          <Group gap="xs">
            <Badge variant="outline">{node.supervisorStrategy}</Badge>
            {node.restartCount > 0 && (
              <Badge color="orange">{node.restartCount} restarts</Badge>
            )}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          {/* Node Details */}
          <Paper withBorder p="sm">
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" fw={500}>
                  Actor ID
                </Text>
                <Text size="xs" color="dimmed">
                  {node.actorId}
                </Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" fw={500}>
                  Supervisor Strategy
                </Text>
                <Text size="xs" color="dimmed">
                  {node.supervisorStrategy}
                </Text>
              </Grid.Col>
              {metrics && (
                <>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>
                      Messages Processed
                    </Text>
                    <Text size="xs" color="dimmed">
                      {metrics.messagesProcessed.toLocaleString()}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>
                      Queue Depth
                    </Text>
                    <Text size="xs" color="dimmed">
                      {metrics.messagesInQueue}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Text size="sm" fw={500}>
                      Error Count
                    </Text>
                    <Text size="xs" color="dimmed">
                      {metrics.errorCount}
                    </Text>
                  </Grid.Col>
                </>
              )}
            </Grid>
          </Paper>

          {/* Children */}
          {children.length > 0 && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Supervised Actors ({children.length})
              </Text>
              <Accordion variant="contained">
                {children.map((childNode) => (
                  <SupervisionNodeAccordion
                    key={childNode.actorId}
                    node={childNode}
                    allNodes={allNodes}
                    actors={actors}
                    actorMetrics={actorMetrics}
                    getHealthColor={getHealthColor}
                  />
                ))}
              </Accordion>
            </div>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
